import { useState, type FormEvent } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { authClient } from '../../services/authClient'
import { ApiClientError } from '../../services/apiClient'
import { AuthShell } from './LoginPage'

type LocationState = {
  email?: string
  purpose?: 'signup' | 'password_reset'
}

export function VerifyOtpPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const navState = (location.state ?? {}) as LocationState
  const email = navState.email ?? ''
  const purpose = navState.purpose ?? 'signup'

  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [resending, setResending] = useState(false)
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null)

  // Si entraron a /verify-otp sin pasar por register/password-reset, vuelvelos al inicio.
  if (!email) {
    return <Navigate to="/login" replace />
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setInfo(null)
    setSubmitting(true)
    try {
      const result = await authClient.verifyOtp({ email, code, purpose })
      if (purpose === 'signup') {
        // signup → result tiene verified_email_token; navegar a completar registro
        const { verified_email_token } = result as unknown as { verified_email_token: string }
        navigate('/register/complete', { state: { verifiedEmailToken: verified_email_token, email }, replace: true })
      } else {
        // password_reset → result tiene reset_ticket
        const ticket = (result as { reset_ticket: string }).reset_ticket
        navigate('/password-reset/confirm', { state: { resetTicket: ticket, email }, replace: true })
      }
    } catch (err) {
      if (err instanceof ApiClientError) {
        const remaining = err.apiError.details?.attempts_remaining
        if (typeof remaining === 'number') {
          setAttemptsRemaining(remaining)
        }
        setError(err.apiError.message)
      } else {
        setError('No se pudo verificar el código. Verifica tu conexión.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  async function handleResend() {
    setError(null)
    setInfo(null)
    setResending(true)
    try {
      const res = await authClient.resendOtp(email, purpose)
      setInfo(res.message)
      setAttemptsRemaining(null)
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.apiError.message)
      } else {
        setError('No se pudo reenviar el código.')
      }
    } finally {
      setResending(false)
    }
  }

  return (
    <AuthShell
      title="Verifica tu correo"
      subtitle={`Ingresa el código de 6 dígitos que enviamos a ${email}`}
    >
      <form onSubmit={handleSubmit} className="auth-form" noValidate>
        <label className="field">
          <span>Código de verificación</span>
          <input
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            required
            autoFocus
            disabled={submitting}
            className="otp-input"
          />
          <small className="muted">El código tiene una vigencia de 10 minutos.</small>
          {attemptsRemaining !== null && (
            <small className="field-error">Intentos restantes: {attemptsRemaining}</small>
          )}
        </label>

        {error && <p className="error">{error}</p>}
        {info && <p className="info">{info}</p>}

        <button type="submit" className="primary-btn" disabled={submitting || code.length !== 6}>
          {submitting ? 'Verificando…' : 'Verificar'}
        </button>

        <button type="button" className="ghost-btn full" onClick={handleResend} disabled={resending}>
          {resending ? 'Reenviando…' : 'Reenviar código'}
        </button>
      </form>
    </AuthShell>
  )
}
